'use client';

import React from 'react';
import Link from 'next/link';
import { CheckCircle2, Flame, Lock, Maximize2, Minimize2, Scale, Sprout, DownloadCloud } from 'lucide-react';
import type { LessonTierVariant } from '@/lib/gamification/contracts/lesson-variants';
import type { LessonTierState } from '@/lib/gamification/services/lesson-tier-state';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type HeaderBadgeTone = 'primary' | 'amber';

interface LearningAppHeaderProps {
  title: string;
  badgeLabel?: string;
  badgeTone?: HeaderBadgeTone;
  leading?: React.ReactNode;
  titleAside?: React.ReactNode;
  center?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

interface LearningAppHeaderButtonProps {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
  title?: string;
}

interface LearningAppReaderControlsProps {
  onDecreaseFont: () => void;
  onIncreaseFont: () => void;
  onToggleWidth: () => void;
  fullWidth: boolean;
  onSaveOffline?: () => void;
  isSavedOffline?: boolean;
  onDownload?: () => void;
}

interface LearningAppTierControlsProps {
  selectedTier: LessonTierVariant | null;
  tierStates: LessonTierState[];
  onSelectTier: (tier: LessonTierVariant) => void;
}

interface LearningAppSegment {
  id: string;
  label: string;
  locked?: boolean;
}

interface LearningAppSegmentTabsProps {
  items: LearningAppSegment[];
  activeId: string | null;
  onSelect: (id: string) => void;
}

const badgeToneClasses: Record<HeaderBadgeTone, string> = {
  primary: 'bg-primary/10 text-primary border-primary/20',
  amber: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
};

const tierIcons: Record<LessonTierVariant, React.ReactNode> = {
  Easy: <Sprout className="w-3.5 h-3.5" />,
  Medium: <Scale className="w-3.5 h-3.5" />,
  Hard: <Flame className="w-3.5 h-3.5" />,
};

const buttonClasses =
  'inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-border/60 bg-background/80 px-1.5 text-[10px] font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:bg-muted/60 hover:text-foreground';

export function LearningAppHeader({
  title,
  badgeLabel,
  badgeTone = 'primary',
  leading,
  titleAside,
  center,
  actions,
  className = '',
}: LearningAppHeaderProps) {
  if (center) {
    return (
      <div className={`grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-y-2 gap-x-1.5 items-center border-b border-border/40 bg-background/88 px-2 py-2 md:px-3 md:py-1.5 backdrop-blur-md ${className}`}>
        <div className="flex min-w-0 items-center gap-1.5 col-start-1 row-start-1">
          {leading ? <div className="shrink-0">{leading}</div> : null}
          <h2 className="truncate text-[12px] font-medium tracking-wide text-foreground">{title}</h2>
          {titleAside ? <div className="flex shrink-0 items-center">{titleAside}</div> : null}
          {badgeLabel ? (
            <span className={`shrink-0 rounded-full border px-1 py-0.5 text-[8px] font-semibold uppercase tracking-[0.16em] ${badgeToneClasses[badgeTone]}`}>
              {badgeLabel}
            </span>
          ) : null}
        </div>
        <div className="flex min-w-0 justify-center overflow-hidden col-span-2 row-start-2 md:col-span-1 md:col-start-2 md:row-start-1">
          {center}
        </div>
        <div className="flex min-w-0 justify-end items-center gap-1.5 col-start-2 row-start-1 md:col-start-3 md:row-start-1">
          {actions}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex min-h-9 items-center gap-1.5 border-b border-border/40 bg-background/88 px-2 py-1.5 backdrop-blur-md md:px-3 ${className}`}>
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {leading ? <div className="shrink-0">{leading}</div> : null}
        <h2 className="truncate text-[12px] font-medium tracking-wide text-foreground">{title}</h2>
        {titleAside ? <div className="flex shrink-0 items-center">{titleAside}</div> : null}
        {badgeLabel ? (
          <span className={`shrink-0 rounded-full border px-1 py-0.5 text-[8px] font-semibold uppercase tracking-[0.16em] ${badgeToneClasses[badgeTone]}`}>
            {badgeLabel}
          </span>
        ) : null}
      </div>
      {actions ? <div className="flex min-w-0 shrink-0 items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

export function LearningAppHeaderButton({
  children,
  className = '',
  href,
  onClick,
  title,
}: LearningAppHeaderButtonProps) {
  if (href) {
    return (
      <Link href={href} className={`${buttonClasses} ${className}`} title={title}>
        {children}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={`${buttonClasses} ${className}`} title={title}>
      {children}
    </button>
  );
}

export function LearningAppReaderControls({
  onDecreaseFont,
  onIncreaseFont,
  onToggleWidth,
  fullWidth,
  onSaveOffline,
  isSavedOffline,
  onDownload,
}: LearningAppReaderControlsProps) {
  return (
    <>
      {onDownload && (
        <Tooltip>
          <TooltipTrigger asChild>
            <LearningAppHeaderButton
              onClick={onDownload}
              className="w-7 px-0 text-primary border-primary/50 hover:bg-primary/10"
            >
              <DownloadCloud className="w-3.5 h-3.5" />
            </LearningAppHeaderButton>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download Markdown</p>
          </TooltipContent>
        </Tooltip>
      )}
      {onSaveOffline && (
        <Tooltip>
          <TooltipTrigger asChild>
            <LearningAppHeaderButton
              onClick={onSaveOffline}
              className={`w-7 px-0 ${isSavedOffline ? 'text-green-500 border-green-500/50 bg-green-500/10' : ''}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
            </LearningAppHeaderButton>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isSavedOffline ? 'Saved for Offline' : 'Cache Offline'}</p>
          </TooltipContent>
        </Tooltip>
      )}
      <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-background/80 p-0.5">
        <button
          type="button"
          onClick={onDecreaseFont}
          className="inline-flex h-6 items-center justify-center rounded-md px-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Decrease font size"
        >
          A-
        </button>
        <button
          type="button"
          onClick={onIncreaseFont}
          className="inline-flex h-6 items-center justify-center rounded-md px-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Increase font size"
        >
          A+
        </button>
      </div>
      <LearningAppHeaderButton
        onClick={onToggleWidth}
        title={fullWidth ? 'Narrow width' : 'Full width'}
        className="w-7 px-0"
      >
        {fullWidth ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
      </LearningAppHeaderButton>
    </>
  );
}

export function LearningAppTierControls({
  selectedTier,
  tierStates,
  onSelectTier,
}: LearningAppTierControlsProps) {
  if (!selectedTier) {
    return null;
  }

  const resolvedTierStates = tierStates.length > 0
    ? tierStates
    : [{ tier: selectedTier, isUnlocked: true, isCompleted: false }];

  return (
    <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-background/80 p-0.5">
      {resolvedTierStates.map((state) => (
        <Tooltip key={state.tier}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => state.isUnlocked && onSelectTier(state.tier)}
              disabled={!state.isUnlocked || resolvedTierStates.length <= 1}
              className={`relative inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent transition-colors ${
                selectedTier === state.tier
                  ? 'text-primary'
                  : state.isUnlocked
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'text-muted-foreground/40 cursor-not-allowed'
              }`}
            >
              {tierIcons[state.tier]}
              {state.isCompleted ? <CheckCircle2 className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 text-primary" /> : null}
              {!state.isUnlocked ? <Lock className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 text-muted-foreground/70" /> : null}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{state.isUnlocked ? state.tier : `${state.tier} locked`}</p>
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

export function LearningAppSegmentTabs({
  items,
  activeId,
  onSelect,
}: LearningAppSegmentTabsProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="flex max-w-full items-center gap-1 overflow-x-auto no-scrollbar">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          className={`inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[9px] font-semibold uppercase tracking-[0.14em] transition-colors ${
            activeId === item.id
              ? 'border-primary/40 bg-primary/10 text-primary'
              : item.locked
              ? 'border-border/50 bg-muted/30 text-muted-foreground/60'
              : 'border-border/60 bg-background/80 text-muted-foreground hover:border-primary/30 hover:text-foreground'
          }`}
        >
          {item.locked ? <Lock className="h-3 w-3 opacity-70" /> : null}
          <span className="whitespace-nowrap">{item.label}</span>
        </button>
      ))}
    </div>
  );
}
